import { z } from 'zod';

import { AgentIdSchema } from '../agent/agent';
import { CardIdSchema } from '../card/card';
import { TaskIdSchema } from '../task/task';

export const HandoffEventIdSchema = z.guid().brand<'HandoffEventId'>();

export type HandoffEventId = z.infer<typeof HandoffEventIdSchema>;

export const HandoffReasonSchema = z.enum([
  'pediu_humano',
  'complexo',
  'low_confidence',
  'policy_violation',
  'sla_risk',
  'escalation',
  'specialist_needed',
  'sla_timeout',
  'round_robin',
  'auto_assignment',
]);

export type HandoffReason = z.infer<typeof HandoffReasonSchema>;

export const HandoffEndpointKindSchema = z.enum(['agent', 'operator', 'system']);

export type HandoffEndpointKind = z.infer<typeof HandoffEndpointKindSchema>;

const HandoffEndpointBaseSchema = z.object({
  agentKind: HandoffEndpointKindSchema,
  agentId: AgentIdSchema.optional(),
});

/**
 * `agentId` must be **omitted** when `agentKind === 'system'` (handoff
 * initiated by a rule or scheduler with no specific agent), and must be
 * **present** when `agentKind === 'agent' | 'operator'`. Mirrors the
 * `$jsonSchema` if/then/else from design v17.
 */
export const HandoffEndpointSchema = HandoffEndpointBaseSchema.superRefine((endpoint, ctx) => {
  if (endpoint.agentKind === 'system') {
    if (endpoint.agentId !== undefined) {
      ctx.addIssue({
        code: 'custom',
        path: ['agentId'],
        message: 'agentId must be omitted when agentKind=system',
      });
    }
  } else if (endpoint.agentId === undefined) {
    ctx.addIssue({
      code: 'custom',
      path: ['agentId'],
      message: 'agentId is required when agentKind != system',
    });
  }
});

export type HandoffEndpoint = z.infer<typeof HandoffEndpointSchema>;

export const HandoffEventSchema = z.object({
  id: HandoffEventIdSchema,
  taskId: TaskIdSchema,
  cardId: CardIdSchema,
  reason: HandoffReasonSchema,
  from: HandoffEndpointSchema,
  to: HandoffEndpointSchema,
  category: z.string().max(50).optional(),
  note: z.string().max(5000).nullish(),
});

export type HandoffEvent = z.infer<typeof HandoffEventSchema>;

export const CreateHandoffEventInputSchema = z.object({
  taskId: TaskIdSchema,
  cardId: CardIdSchema,
  reason: HandoffReasonSchema,
  from: HandoffEndpointSchema,
  to: HandoffEndpointSchema,
  category: z.string().max(50).optional(),
  note: z.string().max(5000).optional(),
});

export type CreateHandoffEventInput = z.infer<typeof CreateHandoffEventInputSchema>;
